import { Box, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProviderBarChartComponent } from './provider-count-bar-chart.component';
import { AnalyticsProvidersQueryParams, ProviderChartTypeArray, ProviderChartTypeEnum } from '@families-link/shared';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import {
	useGetProvidersPerLanguageLinguistLinkQuery,
	useGetProvidersPerLanguageQuery,
} from '../../../../store/api/engagements.api';
import { useGetAnalyticsProvidersQuery } from '../../../../store/api/analytics.api';
import dayjs from 'dayjs';
import { FilterValuesStrings } from '../../pages/analytics.page';
import { FadeSpinner } from '../../../_core/components/_ui/fade-spinner';

export type ProviderFullDateChartProps = {
	transformedFilterValues: FilterValuesStrings;
	currentDistrictId?: string | null;
	authToken?: string;
};

export const ProviderFullDateChart: FC<ProviderFullDateChartProps> = (props) => {
	const { currentDistrictId, transformedFilterValues, authToken } = props;
	const [t] = useTranslation();
	const [currentChartType, setCurrentChartType] = useState(ProviderChartTypeEnum.Language);
	const normalArgs: AnalyticsProvidersQueryParams = {
		chartType: currentChartType,
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(transformedFilterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(transformedFilterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		schools: transformedFilterValues?.schoolIds,
		district: currentDistrictId,
		districts: transformedFilterValues?.districtIds,
		services: transformedFilterValues?.serviceIds,
		programs: transformedFilterValues?.programIds,
		providers: transformedFilterValues?.providerIds,
		languages: transformedFilterValues?.languages,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
		engagementTypes: transformedFilterValues?.engagementTypes,
	};

	const authenticatedNormalArgs = useMemo(
		() => ({
			queryParams: normalArgs,
			authToken,
		}),
		[normalArgs, authToken]
	);

	const providersQuery = useGetAnalyticsProvidersQuery(authenticatedNormalArgs, { refetchOnMountOrArgChange: true });

	const data = providersQuery.data;
	const isFetching = providersQuery.isFetching;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width: '100%',
				height: '100%',
				gap: 2,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
				<Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#797F92' }}>Providers</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', ml: 'auto' }}>
					{ProviderChartTypeArray.map((value: ProviderChartTypeEnum) => (
						<CoreButton
							sx={
								currentChartType === value
									? {
											fontSize: '12px',
											p: 1,
											height: '24px',
											bgcolor: (theme) => theme.colors.lightBlue,
											color: (theme) => theme.colors.white,
											'&:hover': {
												bgcolor: (theme) => theme.colors.lightBlue,
												borderWidth: '1px',
											},
									  }
									: {
											fontSize: '12px',
											p: 1,
											height: '24px',
											'&:hover': {
												color: (theme) => theme.colors.lightBlue,
												borderWidth: '1px',
											},
									  }
							}
							variant="base"
							onClick={() => setCurrentChartType(value)}
						>
							{value}
						</CoreButton>
					))}
				</Box>
			</Box>
			{isFetching ? (
				<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
					<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
				</Box>
			) : (
				<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{data && data.length > 0 ? (
						<ProviderBarChartComponent data={data} />
					) : (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
								No data found for the chart
							</Typography>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};
